import { openModalOrRedirect } from '@/pages-signup-modal/interception.helpers'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { handleSignupCtaClick } from '@/pages-signup-modal/cta-interception'

let signupModalShownOnScroll = false
let signupModalShownOnExit = false
let scrollMilestoneReached = false
let exitIntentTriggered = false

/**
 * Checks if the signup modal can be shown. The modal can be shown if the following conditions are met;
 * - the user is not authenticated (no auth token in local storage)
 * - modal has not be shown for that particular trigger
 */
function canShowSignupModal(trigger: 'scroll' | 'exit' | 'cta'): boolean {
  const isAuthenticated = Boolean(storage.getItem(StorageKeys.authToken))

  // User is authenticated
  if (isAuthenticated) {
    return false
  }

  // For scroll and exit intent triggers, check if the modal has not been shown with the specific trigger.
  // We always show for cta interception trigger.
  if (trigger !== 'cta') {
    const hasShownModalForScrollOrExitTrigger =
      trigger === 'scroll' ? signupModalShownOnScroll : signupModalShownOnExit
    if (hasShownModalForScrollOrExitTrigger) {
      return false
    }
  }

  return true
}

/**
 * Shows the signup modal for a particular trigger if the conditions are met.
 * See {@link canShowSignupModal}
 */
function showSignupModal(trigger: 'scroll' | 'exit'): void {
  if (canShowSignupModal(trigger)) {
    const signupUrl = new URL('/app/signup', window.location.href)
    void openModalOrRedirect(signupUrl, () => {})
  }
}

/**
 * Shows the signup modal when the user has scrolled 30% of the pages content.
 */
function showSignupModalOnScroll(): void {
  if (scrollMilestoneReached) {
    return
  }

  const scrollableHeight =
    document.documentElement.scrollHeight - window.innerHeight

  if (scrollableHeight <= 0) {
    return
  }

  const scrolledPercent = window.scrollY / scrollableHeight

  if (scrolledPercent >= 0.3) {
    scrollMilestoneReached = true
    showSignupModal('scroll')
    signupModalShownOnScroll = true
  }
}

/**
 * Shows the signup modal when the user makes an exit intent.
 * The exit intent is determined when the user moves their cursor
 * outside the page towards the top of the browser.
 */
function showSignupModalOnExitIntent(event: MouseEvent): void {
  if (exitIntentTriggered) {
    return
  }

  // A null `relatedTarget` is the signal that the pointer left the document
  // entirely, and `clientY <= 0` restricts it to the top edge.
  if (!event.relatedTarget && event.clientY <= 0) {
    exitIntentTriggered = true
    showSignupModal('exit')
    signupModalShownOnExit = true
  }
}

/**
 * Setup the event listeners required to trigger the signup modal for
 * the scroll trigger, exit intent trigger and signup cta clicks.
 */
export function setupSignupModalEventListeners() {
  window.addEventListener('scroll', showSignupModalOnScroll, {
    passive: true,
  })
  document.addEventListener('mouseout', showSignupModalOnExitIntent)

  // We setup the event listener to intercept the cta clicks that show the
  // signup modal if the conditions are met.
  if (canShowSignupModal('cta')) {
    document.addEventListener('click', handleSignupCtaClick, true)
  }

  return (): void => {
    window.removeEventListener('scroll', showSignupModalOnScroll)
    document.removeEventListener('mouseout', showSignupModalOnExitIntent)
    document.removeEventListener('click', handleSignupCtaClick, true)
  }
}
